import { Injectable } from '@angular/core';
import { AppStore, SetInfoDrawerMetadataAspectAction, SetInfoDrawerStateAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { NavigationEnd, Router } from '@angular/router';
import { debounceTime, filter } from 'rxjs/operators';
import { AppConfigService, AppConfigValues, LanguageService, LanguageItem, UserPreferenceValues, UserPreferencesService } from '@alfresco/adf-core';
import { TranslateService } from '@ngx-translate/core';
import { LoginService } from './login.service';
import { InitState } from '../models/init-state';
import { registerLocaleData } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeCa from '@angular/common/locales/ca';

@Injectable({
  providedIn: 'root'
})
export class InitService {

  //plugins: { [pluginId: string]: boolean | string };

  initState: InitState;

  LOCALE_MAP: Record<string, any> = {
    en: localeEn,
    es: localeEs,
    ca: localeCa
  };

  private routerSub?: Subscription;
  public languageSub?: Subscription;

  private languagesLoaded: boolean;
  private initialUserLocale: string;
  private finalUserLocale: string;
  private languages: LanguageItem[];

  constructor(
    private router: Router,
    private store: Store<AppStore>,
    private appConfigService: AppConfigService,
    private languageService: LanguageService,
    private userPreferencesService: UserPreferencesService,
    private translate: TranslateService,
    private loginService: LoginService,
    private titleService: Title
  ) {
    //Sirve para aplicar la configuración cada vez que accedes al viewer de documentos, independientemente desde donde accedes y el action usado.
    if(!this.routerSub) {
      this.routerSub = this.router.events.pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((next: NavigationEnd) => {
          if (next.url.includes("viewer:view")) {
            this.updateState();
          }
        });
    }

    // Para escuchar si viene el ticket en la url o en una cookie en todas las rutas
    this.loginService.listenEcmTicket();

    // En este momento devuelve undefined, y con onLoad no devuelve los nuestros
    // this.plugins = this.appConfigService.get(APP_CONFIG_PLUGINS);
  }

  /**
   * Inicializa la aplicación utilizando el estado de configuración proporcionado.
   *
   * Este método ejecuta las tareas necesarias para preparar la aplicación antes
   * de que quede operativa: carga de configuraciones, inicialización de servicios,
   * recuperación de datos base, etc. El parámetro `initState` permite personalizar
   * el proceso de arranque según el contexto.
   *
   * @param initState (Opcional) Estado inicial utilizado para configurar la aplicación.
   * @returns Promise que se resuelve cuando la inicialización ha finalizado.
   */
  initApp(initState?: InitState): Promise<void> {
    //console.debug("INIT APP", initState);
    return new Promise(async (resolve) => {
      this.initState = initState ?? new InitState();
      await this.loginService.checkLocalECMTicket();
      this.updateState();
      this.setLanguages(!!initState);
      this.setTitle();
      this.initializeProfileVisibility();
      resolve();
    });
  }

  initializeProfileVisibility() {
    if (this.initState.hideProfile) {
        const style = document.createElement('style');
        style.innerHTML = `
            a.adf-userinfo__detail-profile {
                display: none !important;
            }
        `;
        document.head.appendChild(style);
    }
  }


  /**
   * Actualiza el estado interno de la aplicación.
   *
   * Este método recalcula y sincroniza las propiedades internas 
   * dependientes de la configuración actual.
   */
  private updateState() {
    const infoDrawerState = this.initState.infoDrawerState;
    const infoDrawerMetadataAspect = this.initState.infoDrawerMetadataAspect;
    ////console.debug("UPDATE STATE", { infoDrawerState, infoDrawerMetadataAspect });

    this.store.dispatch(new SetInfoDrawerStateAction(infoDrawerState));
    this.store.dispatch(new SetInfoDrawerMetadataAspectAction(infoDrawerMetadataAspect));
  }

  /**
   * Configura los idiomas disponibles en la aplicación.
   *
   * Este método inicializa la lista de idiomas soportados y establece
   * las configuraciones necesarias para la internacionalización, como
   * carga de traducciones o asignación del idioma activo.
   */
  public setLanguages(forceLanguages?: boolean) {
    //console.debug("SET LANGUAGES", this.initState.languages);

    // "languages.$replace" no funciona, los concatena
    let languages = this.initState.languages;
    if(!languages || languages.length === 0)
      languages = [{ key: 'es', label: 'Español' }, { key: 'en', label: 'English' }];

    ////console.debug("Final Languages", languages);
    this.languages = languages;
    this.languageService.setLanguages(this.languages);

    // Registrar Locales para evitar el error: Missing locale data for the locale "ca".' for pipe 'DatePipe'
    this.languages.forEach(lang => {
      const locale = this.LOCALE_MAP[lang.key];
      if (locale) {
        registerLocaleData(locale, lang.key);
      }
    });

    if(!this.languagesLoaded) {
      //console.debug("Listening changes in language");
      this.languageSub = this.translate.onLangChange?.pipe(debounceTime(50)).subscribe((event) => {
        //console.debug("LANGUAGE CHANGED: " + event.lang);
        if (this.finalUserLocale && this.finalUserLocale !== event.lang) {
          //console.debug("LANGUAGE FORCED TO: " + this.finalUserLocale);
          this.translate.use(this.finalUserLocale);
        }
      });
    }

    if(!this.languagesLoaded || forceLanguages) {
      this.loadLanguage(forceLanguages);
    }

    if(!this.languagesLoaded) {
      //console.debug("Listening changes in userPreferenceStatus");
      this.userPreferencesService.onChange.subscribe((userPreferenceStatus) => {
        const locale = userPreferenceStatus.locale;
        if(locale !== this.finalUserLocale) { 
          //console.debug("Actual Alden Locale '" + this.finalUserLocale + "' and User Preferences Locale has change to '" + locale + "'");
          this.finalUserLocale = locale;
        }
      });
    }

    this.languagesLoaded = true;
  }

  private loadLanguage(forceLanguage?: boolean): string {
    //console.debug(`========== LOAD LANGUAGE ${forceLanguage ? "FORCED" : ""} ==========`);

    // Necesario comprobar si está en languages por si cambias de un Alden a otro
    const userPreferencesLocale = this.userPreferencesService.locale;
    //console.debug("User Preferences Locale", userPreferencesLocale);

    let userLocale = userPreferencesLocale;
    if(!this.languagesLoaded) {
      this.initialUserLocale = userLocale;
      //console.debug("Initial User Locale", this.initialUserLocale);
    }else if(forceLanguage) {
      //console.debug("Force userLocale '" + userLocale + "' to '" + this.initialUserLocale + "'");
      userLocale = this.initialUserLocale;
    }

    //console.debug("User Locale", userLocale);
    //console.debug("Default Locale (Browser)", this.getDefaultLocale());

    let locale = userLocale || this.getDefaultLocale();
    if (!this.languages.some((l) => l.key === locale)) {
      // si tiene '-', obtenemos solo lo de antes
      if(locale.includes('-')) {
        locale = locale.split('-')[0];
        if (!this.languages.some((l) => l.key === locale))
          locale = this.languages.some((l) => l.key === 'es') ? 'es' : this.languages.some((l) => l.key === 'en') ? 'en' : this.languages[0].key;
      } else {
        locale = this.languages.some((l) => l.key === 'es') ? 'es' : this.languages.some((l) => l.key === 'en') ? 'en' : this.languages[0].key;
      }
    }

    this.finalUserLocale = locale;
    //console.debug("Final locale", this.finalUserLocale);

    // Necesario porque si no, entra inicialmente (initUserLanguage) con setWithoutStore (sin guardarse), asique se pone en ingles (la primera vez o cuando abres incognito)
    if(userPreferencesLocale != locale || forceLanguage) {
      this.userPreferencesService.locale = locale;
      //console.debug("Updating userLocale '" + userPreferencesLocale + "' to '" + locale + "'");
      this.userPreferencesService.set(UserPreferenceValues.Locale, locale);
      this.userPreferencesService.set('textOrientation', this.getLanguageByKey(locale).direction || 'ltr');
    }

    return locale;
  }

  /**
   * Obtiene el locale por defecto de la aplicación.
   *
   * Devuelve el locale base que debe utilizarse cuando no existe una
   * configuración explícita del usuario. Normalmente se deriva del
   * idioma principal o de la configuración inicial de la app.
   *
   * @returns Locale por defecto en formato string.
   */
  private getDefaultLocale(): string {
    // Necesario cambiar el comportamiento para que mande el idioma del navegador, y no el de por defecto del proyecto
    return this.translate.getBrowserCultureLang() || this.appConfigService.get<string>(UserPreferenceValues.Locale) || 'es';
  }

  /**
   * Devuelve la información del idioma asociado a la clave indicada.
   *
   * Busca dentro de la lista de idiomas configurados y devuelve el
   * objeto `LanguageItem` correspondiente al identificador recibido.
   * Si no existe coincidencia, puede devolver un valor por defecto.
   *
   * @param key Identificador del idioma.
   * @returns Objeto `LanguageItem` asociado a la clave.
   */
  public getLanguageByKey(key: string): LanguageItem {
    // Necesario porque este metodo en userPreferencesService es privado y además el por defecto es en ingles
    return (
      this.appConfigService.get<Array<LanguageItem>>(AppConfigValues.APP_CONFIG_LANGUAGES_KEY, [{ key: 'es' } as LanguageItem])
        ?.find((language) => key?.includes(language.key)) || { key: 'es' } as LanguageItem
    );
  }

  /**
   * Establece el título de la página.
   *
   * Busca el título inicial en el objeto `initState`. Si no se encuentra un título válido,
   * utiliza el valor por defecto `'ALDEN'`. Luego, establece el título utilizando el servicio `titleService`.
   *
   * @param key Identificador del idioma (no utilizado en este método).
   * @returns void
   */
  private setTitle() {
    this.titleService.setTitle(this.initState.title ?? 'ALDEN');
  }

}